import type { SungNote } from '../vocal/types.js'
import type { RawNote } from './quantize.js'

/**
 * The two adapters between a captured performance and the scorers - pure, and small on purpose.
 *
 * The Melody Trainer runs voice, instrument and MIDI through ONE scoring path: `alignNotes` and
 * `scorePhrase` for pitch, `scoreTiming` for timing. Three code paths would be three sets of rules
 * about what a wrong note is, and they would drift. So the two modalities that do not already
 * produce the scorers' input shape are converted here, in six lines each, where the conversion is
 * visible instead of buried in a component.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6
 */

/**
 * A MIDI performance as sung notes.
 *
 * `cents` and `iqrCents` are 0 because a MIDI key HAS no tuning and no vibrato - they are not
 * measurements of a perfectly steady player. Two consequences a reader should know about:
 * `scorePhrase` reports every paired MIDI note as `on` or `wrong-note` and nothing in between,
 * which is §6's "exact semitone match per DTW pair"; and every MIDI note reports `steady`, which
 * is true in the only sense the word can have here.
 *
 * Onsets are already relative to the take's beat 1 (`midiEventsToRawNotes` re-bases them), so a
 * player who came in ahead of the downbeat keeps a negative start rather than being clamped to it
 * - the same rule the quantizer holds, and for the same reason: a clamped onset lies about when
 * the note was played.
 */
export const rawNotesToSungNotes = (raw: RawNote[]): SungNote[] =>
  raw.map(note => ({
    startTime: note.onsetMs / 1000,
    endTime: (note.onsetMs + note.durationMs) / 1000,
    midi: note.midi,
    cents: 0,
    iqrCents: 0,
  }))

/**
 * Segmented audio notes as onsets in ms from the take's beat 1.
 *
 * `startTime` is the note's FIRST VOICED FRAME, scoop included. The scoop is discarded before the
 * note's pitch is measured (vocal spec §5.2 - singers slide into pitch and scoring the slide would
 * penalise the ones doing it deliberately) and it is exactly what must NOT be discarded here,
 * because it is when the singer started the note. Measuring the onset from the post-scoop frames
 * would report every singer as 100 ms late.
 */
export const segmentOnsetsMs = (segments: SungNote[], t0Ms: number): number[] =>
  segments.map(segment => segment.startTime * 1000 - t0Ms)
